import React, { useEffect, useState } from "react";

import { BaseControlProps } from "../../models/BaseControlProps";
import { payslipChangeAutdits } from "../../models/PayslipChangeAudits";
import { useAuth } from "../../services/AuthContext";
import { useMessage } from "../../components/Common/MessageContext/MessageContext";
import { UserAPIService } from "../../services/UserAPIService";
import { UserPayslipChangeReportRequest } from "../../models/UserPayslipChangeReportRequest";
import { Box, Button, Card, CardContent, CircularProgress, Divider, FormControl, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, Skeleton, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { DataTable, Pager, Span } from "@tag/tag-components-react-v4";
import { UtilService } from "../../services/UtilService";
import { DatePicker } from '@mui/x-date-pickers';
import moment from "moment";
import dayjs, { Dayjs } from "dayjs";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
        },
    },
};

const PayslipChangeReport = (props: BaseControlProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const pageSize = 15;
    const [page, setPage] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const [loading, isLoading] = useState<boolean>(true);
    const [downloading, setDownloading] = useState<boolean>(false);
    const [report, setReport] = useState<payslipChangeAutdits[]>([]);
    const authContext = useAuth();
    const { showMessage } = useMessage();
    const apiService = new UserAPIService();
    const [scopeGet, applyGet] = useState<UserPayslipChangeReportRequest>({
        pageSize: pageSize,
        pageNo: 0,
        filterSearchString: "",
        orderByString: "",
    });
    const [usernameStr, setUsernameStr] = useState<string>('');
    const [meppnoStr, setMeppnoStr] = useState<string>('');
    const [requestType, setRequestType] = useState<string>('');
    const [dateRange, setDateRange] = useState<{
        start: Dayjs | null;
        end: Dayjs | null;
    }>({ start: null, end: null});

    const getPayslipChangeReport = () => {
        isLoading(true);
        scopeGet.filterSearchString = getSeachFilters();
        scopeGet.orderByString = getOrderByFilters();
        applyGet(scopeGet);
        apiService.getPayslipChangeReport(scopeGet, authContext.token).then((response) => {
            if (response.error) {
                if(props.onMessageNeeded) {
                    props.onMessageNeeded(UtilService.getErrorMessageInfo(response.error));
                }
                showMessage(response.error, 'error');
            }
            else {
                setReport(response.response?.payslipChangeAudits ?? []);
                const pageCount =
                    response.response && response.response?.payslipChangeAudits.length > 0
                        ? Math.ceil(response.response.totalCount / pageSize)
                        : 0;
                setPageCount(pageCount && pageCount > 0 ? pageCount : 0);
            }
            isLoading(false);
        })
    }

    const getPayslipChangeReportForDownload = () => {
        setDownloading(true);
        scopeGet.filterSearchString = getSeachFilterForReport();
        scopeGet.orderByString = getOrderByFilters();
        applyGet(scopeGet);
        apiService.getPayslipChangeReport(scopeGet, authContext.token).then((response) => {
            if (response.error) {
                if(props.onMessageNeeded) {
                    props.onMessageNeeded(UtilService.getErrorMessageInfo(response.error));
                }
                showMessage(response.error, 'error');
            }
            else {
                const csvData = convertToCSV(response.response && response.response.payslipChangeAudits ? response.response?.payslipChangeAudits : []);
                const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                const formattedDateTime = moment(new Date()).format("DD-MM-YYYY_HH-mm");

                link.href = url;
                link.setAttribute('download', `payslip_change_data_${formattedDateTime}.csv`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
            setDownloading(false);
        })
    }

    const getOrderByFilters = () => {
        return  `{
            "OrderByFields": [
              {
                "FieldName": "createdOn",
                "OrderDirection": "DESC"
              }
            ]
        }`
    }

    const getSeachFilters = () => {
        let searchFilters = [];
        if (requestType.trim() !==  "") {
            searchFilters.push(`{"FieldName":"requestType","SearchType":"equals","SearchValue":"${requestType}"}`);
        }

        
        if (meppnoStr.trim() !== "") {
            searchFilters.push(`{"FieldName":"peopleXDId","SearchType":"contains","SearchValue":"${meppnoStr}"}`);
        }

        if (usernameStr.trim() !== "") {
            searchFilters.push(`{"FieldName":"userName","SearchType":"equals","SearchValue":"${usernameStr}"}`);
        }

        if (dateRange.start !== null && dateRange.end != null) {
            const dateStart = moment(dateRange.start.toISOString())
                .startOf('day')
                .format('YYYY-MM-DD HH:mm:ss.SSS');
            const dateEnd = moment(dateRange.end.toISOString())
                .endOf('day')
                .format('YYYY-MM-DD HH:mm:ss.SSS');
            searchFilters.push(`{"FieldName":"AuditDate","SearchType":"greaterthanequal","SearchValue":"${dateStart}"}`);
            searchFilters.push(`{"FieldName":"AuditDate","SearchType":"lessthanequal","SearchValue":"${dateEnd}"}`);
        }

        return searchFilters.length >= 0 ? `{"SearchFilters": [${searchFilters.join(",")}]}` : '';
    }

    const getSeachFilterForReport = () => {
        let searchFilters = [];

        if (dateRange.start == null || dateRange.end == null) {
            const reportDate = moment()
                .subtract(30, 'days')
                .startOf('day')
                .format('YYYY-MM-DD HH:mm:ss.SSS');
    
            searchFilters.push(`{"FieldName":"AuditDate","SearchType":"greaterthanequal","SearchValue":"${reportDate}"}`);
        }
        else {
            const dateStart = moment(dateRange.start.toISOString())
                .startOf('day')
                .format('YYYY-MM-DD HH:mm:ss.SSS');
            const dateEnd = moment(dateRange.end.toISOString())
                .endOf('day')
                .format('YYYY-MM-DD HH:mm:ss.SSS');
            searchFilters.push(`{"FieldName":"AuditDate","SearchType":"greaterthanequal","SearchValue":"${dateStart}"}`);
            searchFilters.push(`{"FieldName":"AuditDate","SearchType":"lessthanequal","SearchValue":"${dateEnd}"}`);
        }

        return searchFilters.length >= 0 ? `{"SearchFilters": [${searchFilters.join(",")}]}` : '';
    }

    const convertToCSV = (objArray: payslipChangeAutdits[] ) => {
        const array = typeof objArray !== 'object' ? JSON.parse(objArray) : objArray;
        let csv = '';

        // Add headers
        csv += Object.keys(array[0]).join(',') + '\n';

        // Add data rows
        array.forEach((item: any) => {
        csv += Object.values(item).map(value => {
            // Handle boolean values
            if (typeof value === 'boolean') {
            return value ? 'true' : 'false';
            }
            // Escape commas in strings
            if (typeof value === 'string' && value.includes(',')) {
            return `"${value}"`;
            }
            return value;
        }).join(',') + '\n';
        });

        return csv;
    };

    const resetFilters = () => {
        setUsernameStr('');
        setMeppnoStr('');
        setRequestType('');
        setDateRange({ start: null, end: null});
    }

    useEffect(() => {
        const fetchPayslipChangeReport = async () => {
            await getPayslipChangeReport();
        }

        fetchPayslipChangeReport();
    }, [])

    useEffect(() => {
        
        if (dateRange.start !== null && dateRange.end == null ) {
            return;
        }

        if (dateRange.end !== null && dateRange.start == null) {
            return;
        }

        // Reset scope
        scopeGet.pageNo = 0;
        scopeGet.pageSize = pageSize;
        applyGet(scopeGet);


        const fetchPayslipChangeReport = async () => {
            await getPayslipChangeReport();
        }

        fetchPayslipChangeReport();
    }, [usernameStr, meppnoStr, requestType, dateRange])

    const columns = React.useMemo(
        () => [
            { 
                accessorKey: 'userId', 
                header: 'User ID'
            },
            {
                accessorKey: 'peopleXDId',
                header: 'MEPP No.'
            },
            { 
                accessorKey: 'requestType', 
                header: 'Type',
                meta: {
                    getCellProps: () => {
                        return { style: { textTransform: 'uppercase'} };
                    },
                },
                
            },
            { 
                accessorKey: 'userName', 
                header: 'Username'
            },
            {  
                header: 'Name',
                cell: ({ row }: any) => {
                    return getUserFullName(row.original);
                }
            },
            { 
                accessorKey: 'auditDate', 
                header: 'Date', 
                meta: { dataType: 'date' },
                cell: ({ row }: any) => {
                    const date = row.original.auditDate;
                    return date == '1900-01-01T00:00:00'
                        ? '-'
                        : moment(date).format('DD-MM-YY HH:mm:ss');
                }, 
            },
        ],
        [],
    );

    const handlePageChange = async (e: any) => {
        setPage(e.page - 1);
        scopeGet.pageNo = e.page - 1;
        
        await getPayslipChangeReport();
    };

    
    const getUserFullName = (payslip: payslipChangeAutdits) => {
        const fullname = payslip.forename + ' ' + payslip.surname;
        return fullname.trim() === '' ? payslip.userName : fullname; 
    }

    const renderheaderView = () => {
        return (
            <Box sx={{ width: '100%', pb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h5" gutterBottom>
                        Payslip Changed Report
                    </Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2} alignItems="center" >
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="Username"
                            variant='outlined'
                            value={usernameStr}
                            onChange={(e) => setUsernameStr(e.target.value)}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="MEPP No."
                            variant='outlined'
                            value={meppnoStr}
                            onChange={(e) => setMeppnoStr(e.target.value)}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel>Type</InputLabel>
                            <Select
                                label="Pay Group"
                                onChange={(e: SelectChangeEvent) => setRequestType(e.target.value)}
                                MenuProps={MenuProps}
                            >
                                <MenuItem key={0} value="">Select a Type</MenuItem>
                                <MenuItem key={1} value="Digital">Digital</MenuItem>
                                <MenuItem key={1} value="PAPER">Paper</MenuItem>
                            </Select>
                        </FormControl> 
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box display="flex" gap={2}>
                            <DatePicker
                                label="Start Date"
                                maxDate={dateRange.end || dayjs()} // Prevent selecting start after end
                                value={dateRange.start}
                                onChange={(date) => setDateRange({ ...dateRange, start: date })}
                            />
                            <DatePicker
                                label="End Date"
                                minDate={dateRange.start || undefined} // Prevent selecting end before start
                                maxDate={dayjs()} // Disable future dates
                                value={dateRange.end}
                                onChange={(date) => setDateRange({ ...dateRange, end: date })}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            size='large'
                            style={{ minWidth: '80px' }}
                            onClick={() =>  {
                                setPage(0);
                                scopeGet.pageNo = 0;
                                getPayslipChangeReport();
                            }}
                        >
                            Filter
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Button
                            fullWidth
                            variant="outlined"
                            size="large"
                            style={{ minWidth: '80px' }}
                            onClick={() => {
                                setPage(0);
                                scopeGet.pageNo = 0;
                                resetFilters();
                                //getPayslipChangeReport();
                            }}
                        >
                            Clear
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Button
                            disabled={downloading}
                            fullWidth
                            variant="outlined"
                            color="secondary"
                            size='large'
                            style={{ minWidth: '80px' }}
                            startIcon={downloading ? <CircularProgress size={20} color="inherit" /> : null}
                            sx={{
                                '&.Mui-disabled': {
                                    color: (theme) => theme.palette.primary.contrastText,
                                }
                            }}
                            onClick={() =>  {
                                getPayslipChangeReportForDownload();
                            }}
                        >
                                {downloading ? 'Downloading...' : 'Download'}
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        );
    }

    const EmptyState = () => (
        <Box 
            sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '150px',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '4px',
            backgroundColor: theme.palette.background.paper,
            }}
        >
            <Typography variant="body1" color="text.secondary">
            No payslips found
            </Typography>
        </Box>
    );

    // Loading skeleton for mobile
    const MobileLoadingSkeleton = () => (
        <Box sx={{ mb: 2 }}>
        {[...Array(3)].map((_, index) => (
            <Card key={index} sx={{ mb: 2 }}>
            <CardContent>
                <Skeleton variant="text" width="60%" height={24} />
                <Skeleton variant="text" width="40%" height={20} />
                <Box display="flex" justifyContent="flex-end">
                <Skeleton variant="rectangular" width={100} height={36} />
                </Box>
            </CardContent>
            </Card>
        ))}
        </Box>
    );

    if (isMobile) {
        return (
            <>
                {renderheaderView()}  

                <div>
                    {loading ? (
                    <MobileLoadingSkeleton />
                        ) : report.length === 0 ? (
                        <EmptyState />
                        ) : (
                        report.map((reportRow) => (
                            <Card key={reportRow.auditDate} sx={{ mb: 1 }}>
                                <CardContent>
                                    <Typography variant="subtitle2">
                                    User ID: {reportRow.userId}
                                    </Typography>
                                    <Typography variant="subtitle2" color="text.secondary">
                                    Username: {reportRow.userName}
                                    </Typography>
                                    <Typography variant="subtitle2" color="text.secondary">
                                    Type: {reportRow.requestType}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                    Forename: {reportRow.forename}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                    Surname: {reportRow.surname}
                                    </Typography>
                                </CardContent>
                            </Card>
                        ))
                    )}
                   {!loading && report.length > 0 && (
                        <Pager
                            accent='blue'
                            page={page + 1}
                            pageCount={pageCount}
                            onPageRequest={(e) => { handlePageChange(e); }}
                            style={{ justifyContent: 'center', padding: '5px', borderBottom: '1px solid #e2ecef' }}
                        />
                    )}
                </div>
            </>
        );
    }

    return(
        <>
            {renderheaderView()}

            <div style={{ background: '#fff' }}>
                <div style={{ height: 'auto' }}>
                    <DataTable 
                        isLoading={loading} 
                        loadingElement={<Span accent="blue">Loading ...</Span>} 
                        accent='blue' 
                        columns={columns} 
                        data={report} 
                    />
                </div>
                <Pager
                    accent='blue'
                    page={page + 1}
                    pageCount={pageCount}
                    onPageRequest={(e) => { handlePageChange(e); }}
                    style={{ justifyContent: 'center', padding: '5px', borderBottom: '1px solid #e2ecef' }}
                />
            </div>
        </>
    )
}

export default PayslipChangeReport;